import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  CircularProgress,
  styled,
} from '@mui/material';
import { motion } from 'framer-motion';
import useCustomToast from '../hooks/useCustomToast';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Styled Components
const PageContainer = styled(Box)({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, rgba(28, 56, 121, 0.9) 0%, rgba(38, 83, 173, 0.9) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url(/hotel-background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.2,
    zIndex: 0,
  }
});

const FormContainer = styled(Container)({
  position: 'relative',
  zIndex: 1,
});

const FormCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    '&:hover fieldset': {
      borderColor: '#FF7700',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#FF7700',
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FF7700',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FF7700',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 24px',
  fontSize: '1.1rem',
  textTransform: 'none',
  background: 'linear-gradient(90deg, #FF7700, #FDB913)',
  color: '#fff',
  fontWeight: 600,
  '&:hover': {
    background: 'linear-gradient(90deg, #FF7700, #FDB913)',
    opacity: 0.9,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(255, 119, 0, 0.3)',
  },
  transition: 'all 0.3s ease',
}));

const BackButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const propertyTypes = [
  'Hotel',
  'Resort',
  'Villa',
  'Lodge',
  'Guest House',
  'Serviced Apartments',
  'Homestay',
  'Cottage/BnB',
];

const PartnerRegistration = () => {
  const navigate = useNavigate();
  const { successToast } = useCustomToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    hotelName: '',
    propertyType: '',
    channelManagerName: '',
    cityName: '',
    managerName: '',
    email: '',
    phone: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Partner Registration Form Data:', formData);
    successToast('Thank you for your interest! We will contact you soon.');
    navigate('/login');
    
    setIsSubmitting(false);
  };

  return (
    <PageContainer>
      <BackButton
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/login')}
      >
        Back to Login
      </BackButton>

      <FormContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FormCard>
            <Typography variant="h4" gutterBottom align="center" sx={{ color: '#2E4057', fontWeight: 600, mb: 4 }}>
              List Your Property for Free and Earn More!
            </Typography>

            <form onSubmit={handleSubmit}>
              <StyledTextField
                fullWidth
                label="Hotel Name"
                name="hotelName"
                value={formData.hotelName}
                onChange={handleChange}
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <StyledSelect
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  label="Property Type"
                  required
                >
                  {propertyTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>

              <StyledTextField
                fullWidth
                label="Channel Manager Name"
                name="channelManagerName"
                value={formData.channelManagerName}
                onChange={handleChange}
              />

              <StyledTextField
                fullWidth
                label="City Name"
                name="cityName"
                value={formData.cityName}
                onChange={handleChange}
                required
              />

              <StyledTextField
                fullWidth
                label="Manager Name"
                name="managerName"
                value={formData.managerName}
                onChange={handleChange}
                required
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <StyledTextField
                  fullWidth
                  label="Email ID"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <StyledTextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Box>

              <SubmitButton
                fullWidth
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit Registration'
                )}
              </SubmitButton>
            </form>
          </FormCard>
        </motion.div>
      </FormContainer>
    </PageContainer>
  );
};

export default PartnerRegistration; 